import { MagnifyingGlassIcon } from '@interstate/components/Icons/MagnifyingGlassIcon';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Autocomplete, Dropdown } from '@makemydeal/dr-shared-components';
import { PhoneInput } from '@makemydeal/dr-shared-components/phone-input';
import { PostalCodeInput } from '@makemydeal/dr-shared-components/postal-code-input';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';

import type { LienholderFormInputsProps } from '../../../../tradeForm/lienholderForm/LienholderFormInputs.interstate';
import { useLienholderField } from '../../../../tradeForm/lienholderForm/useLienholderField';
import { useLienholderFormInputs } from '../../../../tradeForm/lienholderForm/useLienholderFormInputs';
import { useScenarioCellId } from '../../../hooks/useScenarioCellId';
import { SearchSuffix, SubRowLabel, SubRowValue } from '../../../ScenarioGrid.style';
import { OUTSIDE_LIENHOLDER_SUB_ROW_KEYS } from './OutsideLienholderExpansion';
import { ScenarioSubRow } from '../../ScenarioSubRow';

export const LIENHOLDER_SEARCH_PLACEHOLDER = 'Search...';

export const OutsideLienholderFields = (props: LienholderFormInputsProps) => {
    const theme = useInterstateTheme();
    const cellId = useScenarioCellId('outsideLienholder');
    const { lienholderFieldProps, addressProps, cityProps, stateProps, zipProps, phoneProps } = useLienholderFormInputs(props);
    const { hasLenders, autocompleteProps, textInputProps } = useLienholderField(lienholderFieldProps);

    const lienholderId = `${cellId}-lienholder`;
    const subRows = [
        {
            id: lienholderId,
            label: autocompleteProps.label,
            field: hasLenders ? (
                <Autocomplete
                    {...autocompleteProps}
                    id={lienholderId}
                    displayLabel={false}
                    placeholder={LIENHOLDER_SEARCH_PLACEHOLDER}
                    inputSuffix={
                        <SearchSuffix theme={theme} data-testid="scenario-lienholder-search-icon">
                            <MagnifyingGlassIcon aria-hidden />
                        </SearchSuffix>
                    }
                />
            ) : (
                <TextInput {...textInputProps} id={lienholderId} displayLabel={false} />
            )
        },
        {
            id: `${cellId}-address`,
            label: addressProps.label,
            field: <TextInput {...addressProps} id={`${cellId}-address`} displayLabel={false} />
        },
        {
            id: `${cellId}-city`,
            label: cityProps.label,
            field: <TextInput {...cityProps} id={`${cellId}-city`} displayLabel={false} />
        },
        {
            id: `${cellId}-state`,
            label: stateProps.label,
            field: <Dropdown {...stateProps} id={`${cellId}-state`} displayLabel={false} />
        },
        {
            id: `${cellId}-zip`,
            label: zipProps.label,
            field: <PostalCodeInput {...zipProps} id={`${cellId}-zip`} displayLabel={false} />
        },
        {
            id: `${cellId}-phone`,
            label: phoneProps.label,
            field: <PhoneInput {...phoneProps} id={`${cellId}-phone`} displayLabel={false} />
        }
    ];

    return (
        <>
            {subRows.map(({ id, label, field }, index) => (
                <ScenarioSubRow key={id} subRowKey={OUTSIDE_LIENHOLDER_SUB_ROW_KEYS[index]}>
                    <SubRowLabel theme={theme} htmlFor={id}>
                        {label}
                    </SubRowLabel>
                    <SubRowValue>{field}</SubRowValue>
                </ScenarioSubRow>
            ))}
        </>
    );
};
